import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Row, Col, Form, Card, Badge } from "react-bootstrap";
import { BsPencil, BsCheck, BsX } from "react-icons/bs";
import { 
    FaUniversity, 
    FaUserTie, 
    FaCreditCard, 
    FaGlobe, 
    FaMoneyBillWave, 
    FaMapMarkerAlt, 
    FaInfoCircle,
    FaExchangeAlt,
    FaBuilding,
    FaHashtag
} from "react-icons/fa";
const BankView = ({ bankData, setBankDetails }) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedBankData, setEditedBankData] = useState(bankData);
    const [setupIdError, setSetupIdError] = useState('');
    const [isSetupIdEditing, setIsSetupIdEditing] = useState(false);
    const [editField, setEditField] = useState(null);
    const [countries, setCountries] = useState([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'setup_id') {
            setIsSetupIdEditing(true);
        }
        setEditedBankData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };
    const fetchCountries = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/countries`, {
                headers: { Authorization: `Bearer ${token}`,
                'Tenant-Origin': origin,

             },
                skipLoader: true,
            });

            let countryList = response.data.Data;

            console.log("API Response Data:", countryList);

            // Ensure only one USD (United States) is included
            countryList = countryList.filter(
                (c) =>
                    c.currency !== "USD" ||
                    (c.name && c.name.toLowerCase().includes("united states"))
            );
    
            // Sort AED first, then USD (United States), then others
            countryList.sort((a, b) => {
                const priority = { AED: 0, USD: 1 }; // Set priority
                const aPriority = priority[a.currency] ?? 2;
                const bPriority = priority[b.currency] ?? 2;
                return aPriority - bPriority;
            });
    
            console.log("Filtered and sorted country list:", countryList.map(c => `${c.country_name || 'Unknown'} - ${c.currency}`));
    
            setCountries(countryList);
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };
    useEffect(() => {
        fetchCountries();

    }, []);

    const handleSave = async () => {
        if (isSetupIdEditing) {
            setSetupIdError('');
        }
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;

            await axios.put(`${backendUrl}/api/bank-accounts/${bankData.id}`, editedBankData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
            });
            setBankDetails((prevDetails) => ({
                ...prevDetails,
                ...editedBankData, // Update only the changed fields
            }));
            setEditField(null);
        } catch (error) {
            if (error.response?.data?.errors?.setup_id) {
                setSetupIdError(error.response.data.errors.setup_id[0]);
            } else {
                console.error('Error saving bank details:', error);
            }
        }
    };
    const getCountryName = (id) => {
        if (!id || countries.length === 0) return "--";
        const country = countries.find((coun) => Number(coun.id) === Number(id));
        return country ? country.currency : "--";
    };
    const fieldGroups = [
        {
            title: "Account Information",
            icon: <FaInfoCircle className="text-primary" />,
            fields: [
                { name: 'name', label: 'Account Name', icon: <FaUniversity size={14} />, type: 'text' },
                { name: 'type', label: 'Account Type', icon: <FaBuilding size={14} />, type: 'text', readOnly: true },
                { name: 'account_holder_name', label: 'Account Holder', icon: <FaUserTie size={14} />, type: 'text' },
                { name: 'account_number', label: 'Account Number', icon: <FaCreditCard size={14} />, type: 'text' },
            ]
        },
        {
            title: "Financial Details",
            icon: <FaMoneyBillWave className="text-success" />,
            fields: [
                { name: 'opening_balance', label: 'Opening Balance', icon: <FaMoneyBillWave size={14} />, type: 'text' },
                { name: 'currency_id', label: 'Currency', icon: <FaGlobe size={14} />, type: 'select', options: countries.map((coun) => ({ value: coun.id, label: coun.currency })), format: getCountryName },
                { name: 'exchange_rate', label: 'Exchange Rate', icon: <FaExchangeAlt size={14} />, type: 'text' },
            ]
        },
        {
            title: "Bank Details",
            icon: <FaUniversity className="text-info" />,
            condition: bankData.type !== "cash account",
            fields: [
                { name: 'branch_name', label: 'Branch Name', icon: <FaBuilding size={14} />, type: 'text' },
                { name: 'iban', label: 'IBAN', icon: <FaHashtag size={14} />, type: 'text' },
                { name: 'swift_code', label: 'SWIFT/BIC Code', icon: <FaHashtag size={14} />, type: 'text' },
                { name: 'bank_address', label: 'Bank Address', icon: <FaMapMarkerAlt size={14} />, type: 'text' },
            ]
        }
    ];

    return (
        <div className="p-3 font-size-13">
            {fieldGroups.map((group, groupIndex) => {
                if (group.condition === false) return null;

                const visibleGroupFields = group.fields.filter(field => {
                    if (bankData.type === "cash account") {
                        return ["name", "opening_balance", "currency_id", "exchange_rate", "type"].includes(field.name);
                    }
                    return true;
                });

                if (visibleGroupFields.length === 0) return null;

                return (
                    <Card key={groupIndex} className="mb-4 shadow-sm border-0 overflow-hidden">
                        <Card.Header className="bg-light border-0 py-3">
                            <div className="d-flex align-items-center">
                                <span className="me-2">{group.icon}</span>
                                <h6 className="mb-0 fw-semibold text-dark">{group.title}</h6>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Row className="g-0">
                                {visibleGroupFields.map((field, fieldIndex) => (
                                    <Col md={6} key={fieldIndex} className="border-bottom border-end">
                                        <div className="p-3">
                                            <div className="d-flex align-items-start">
                                                <div className="text-muted me-3 mt-1">
                                                    {field.icon}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <label className="text-muted small mb-1 d-block fw-medium">
                                                        {field.label}
                                                    </label>
                                                    <div className="editable-container">
                                                        {editField === field.name ? (
                                                            <div className="d-flex align-items-center">
                                                                {field.type === "select" ? (
                                                                    <Form.Select
                                                                        name={field.name}
                                                                        value={editedBankData[field.name] || ""}
                                                                        onChange={handleChange}
                                                                        autoFocus
                                                                        className="form-control form-control-sm"
                                                                    >
                                                                        <option value="">Select</option>
                                                                        {field.options.map((option) => (
                                                                            <option key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>
                                                                ) : (
                                                                    <Form.Control
                                                                        type="text"
                                                                        name={field.name}
                                                                        value={editedBankData[field.name] || ""}
                                                                        onChange={handleChange}
                                                                        autoFocus
                                                                        className="form-control form-control-sm"
                                                                    />
                                                                )}
                                                                <BsCheck className="text-success ms-2 cursor-pointer" onClick={handleSave} size={18} />
                                                                <BsX className="text-danger ms-1 cursor-pointer" onClick={() => setEditField(null)} size={18} />
                                                            </div>
                                                        ) : (
                                                            <div 
                                                                className="d-flex align-items-center group-hover-pencil cursor-pointer"
                                                                onClick={() => !field.readOnly && setEditField(field.name)}
                                                            >
                                                                <span className="text-dark fw-semibold me-2">
                                                                    {field.name === "currency_id"
                                                                        ? getCountryName(editedBankData[field.name])
                                                                        : field.name === "type" 
                                                                            ? (editedBankData[field.name] === "cash account" ? "Cash Account" : "Bank Account")
                                                                            : editedBankData[field.name] || "-"}
                                                                </span>
                                                                {!field.readOnly && <BsPencil className="text-primary opacity-0 pencil-hover-show" size={12} />}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Card.Body>
                    </Card>
                );
            })}
            
            <style>{`
                .group-hover-pencil:hover .pencil-hover-show {
                    opacity: 1 !important;
                }
                .group-hover-pencil {
                    min-height: 24px;
                }
            `}</style>
        </div>
    );
};

export default BankView;
